import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  tourContent: {
    padding: '20px',
    maxWidth: '400px',
  },
  tourTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '12px',
    color: '#1F2937',
  },
  tourDescription: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#6B7280',
    marginBottom: '20px',
  },
  tourActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  stepCounter: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
  tourNavButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface TourContentProps {
  currentStep: number;
  totalSteps: number;
  setIsOpen: (isOpen: boolean) => void;
  setCurrentStep: (step: number) => void;
  content: string;
}

const TourContent: React.FC<TourContentProps> = ({
  currentStep,
  totalSteps,
  setIsOpen,
  setCurrentStep,
  content,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const switchToChartTab = () => {
    const event = new CustomEvent('tour-switch-tab', { 
      detail: { tabKey: 'charts' },
    });
    window.dispatchEvent(event);
  };

  const handleNext = () => {
    if (currentStep === 0) {
      const event = new CustomEvent('tour-switch-layout');
      window.dispatchEvent(event);
      return;
    }
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      switchToChartTab();
      setIsOpen(false);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    switchToChartTab();
    setIsOpen(false);
  };

  return (
    <div className={classes.tourContent}>
      <div className={classes.tourTitle}>
        {t(`tour.step${currentStep + 1}Title`)}
      </div>
      <div className={classes.tourDescription}>
        {t(content)}
      </div>
      <div className={classes.tourActions}>
        <div className={classes.stepCounter}>
          {currentStep + 1}/{totalSteps}
        </div>
        <div className={classes.tourNavButtons}>
          <Button type="text" onClick={handleSkip}>
            {t('tour.skip')}
          </Button>
          {currentStep > 0 && (
            <Button onClick={handlePrev}>
              {t('tour.previous')}
            </Button>
          )}
          <Button type="primary" onClick={handleNext}>
            {currentStep === totalSteps - 1 ? t('tour.finish') : t('tour.next')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourContent;

